import React from 'react';

// Real public commit activity, aggregated from the actual commit dates of the
// 17 public SHAILY24 repositories via `gh api repos/SHAILY24/<repo>/commits`
// (committer date, grouped by calendar month), trailing 24-month window.
// 148 public commits, fetched 2026-05-17. No smoothing, no fabricated series:
// the quiet months (0) are shown as gaps, not hidden. This is a measured
// artifact, not decoration.

const SERIES = [
  ['2024-05', 0], ['2024-06', 0], ['2024-07', 0], ['2024-08', 4],
  ['2024-09', 8], ['2024-10', 8], ['2024-11', 11], ['2024-12', 5],
  ['2025-01', 8], ['2025-02', 7], ['2025-03', 8], ['2025-04', 8],
  ['2025-05', 5], ['2025-06', 6], ['2025-07', 7], ['2025-08', 9],
  ['2025-09', 0], ['2025-10', 0], ['2025-11', 33], ['2025-12', 0],
  ['2026-01', 3], ['2026-02', 13], ['2026-03', 3], ['2026-04', 2],
];

const TOTAL = SERIES.reduce((s, [, c]) => s + c, 0);
const MAX = Math.max(...SERIES.map(([, c]) => c));

const W = 640;
const H = 116;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 22;

function monthLabel(ym) {
  const [y, m] = ym.split('-');
  const names = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${names[+m - 1]} ${y.slice(2)}`;
}

export default function CommitChart() {
  const n = SERIES.length;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const gap = 3;
  const barW = innerW / n - gap;
  const [hover, setHover] = React.useState(null);

  const bars = SERIES.map(([ym, c], i) => {
    const x = PAD_L + i * (innerW / n) + gap / 2;
    const h = MAX ? (c / MAX) * innerH : 0;
    const y = PAD_T + innerH - h;
    return { ym, c, x, y, h, i };
  });

  const firstLabel = monthLabel(SERIES[0][0]);
  const lastLabel = monthLabel(SERIES[n - 1][0]);
  const active = hover != null ? bars[hover] : null;

  return (
    <figure className="figure commitfig">
      <figcaption>
        public commit activity
        <span className="src">
          {' '}
          measured from the 17 SHAILY24 repositories, last 24 months
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Bar chart of public commit activity by month across the 17 SHAILY24 repositories: ${TOTAL} commits over the last 24 months, ranging from ${firstLabel} to ${lastLabel}. Quiet months are shown as gaps, not smoothed.`}
        preserveAspectRatio="xMidYMid meet"
      >
        <line
          className="ch-base"
          x1={PAD_L}
          y1={PAD_T + innerH + 0.5}
          x2={W - PAD_R}
          y2={PAD_T + innerH + 0.5}
        />
        {bars.map((b) => (
          <rect
            key={b.ym}
            className={`ch-bar${b.c === 0 ? ' zero' : ''}${
              hover === b.i ? ' on' : ''
            }`}
            x={b.x}
            y={b.c === 0 ? PAD_T + innerH - 1.5 : b.y}
            width={barW}
            height={b.c === 0 ? 1.5 : b.h}
            rx="1"
            onMouseEnter={() => setHover(b.i)}
            onMouseLeave={() => setHover(null)}
          >
            <title>{`${monthLabel(b.ym)}: ${b.c} commit${
              b.c === 1 ? '' : 's'
            }`}</title>
          </rect>
        ))}
        <text className="ch-axis" x={PAD_L} y={H - 6} textAnchor="start">
          {firstLabel}
        </text>
        <text
          className="ch-axis"
          x={W - PAD_R}
          y={H - 6}
          textAnchor="end"
        >
          {lastLabel}
        </text>
      </svg>
      <div className="ch-readout" aria-live="polite">
        {active ? (
          <span>
            <b className="num">{active.c}</b> commit
            {active.c === 1 ? '' : 's'} in {monthLabel(active.ym)}
          </span>
        ) : (
          <span>
            <b className="num">{TOTAL}</b> public commits over 24 months ·
            hover a month
          </span>
        )}
      </div>
    </figure>
  );
}
